import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Play, Users, MessageCircle, Heart, Sparkles } from 'lucide-react';
import axios from 'axios';
import LoadingAnimation from './LoadingAnimation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GIRAFFE_MASCOT = '/images/simby-mascote.png';

const SimulatorsPage = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const response = await axios.get(`${API}/simulators`, { withCredentials: true });
        setScenarios(response.data.map(s => ({
          id: s.scenario_id || s.lesson_id,
          ...s
        })));
      } catch (error) {
        console.error('Error fetching scenarios:', error);
        setScenarios([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchScenarios();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <div className="text-center">
          <LoadingAnimation size="md" className="mx-auto mb-4" />
          <p className="text-gray-600">Carregando simuladores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <img
            src={GIRAFFE_MASCOT}
            alt="Simby"
            className="w-32 h-32 mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Simuladores de Conversas em Inteligência Relacional
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Pratique Comunicação Não-Violenta em cenários realistas.
            Converse com a IA assumindo diferentes papéis e receba feedback personalizado.
          </p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="p-6 text-center bg-gradient-to-br from-purple-50 to-white">
            <div className="text-4xl mb-2">🎭</div>
            <div className="text-3xl font-bold text-purple-600 mb-1">{scenarios.length}</div>
            <div className="text-sm text-gray-600">Cenários Disponíveis</div>
          </Card>
          <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white">
            <div className="text-4xl mb-2">💬</div>
            <div className="text-3xl font-bold text-blue-600 mb-1">5</div>
            <div className="text-sm text-gray-600">Mensagens por Conversa</div>
          </Card>
          <Card className="p-6 text-center bg-gradient-to-br from-green-50 to-white">
            <div className="text-4xl mb-2">⭐</div>
            <div className="text-3xl font-bold text-green-600 mb-1">200</div>
            <div className="text-sm text-gray-600">XP por Cenário</div>
          </Card>
        </div>

        {/* Special Feature: Heart of Action */}
        <Card className="mb-8 overflow-hidden bg-gradient-to-r from-purple-100 via-blue-50 to-purple-100 border-2 border-purple-300">
          <div className="p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg">
                <Heart className="w-12 h-12 text-white" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-semibold text-purple-600 uppercase tracking-wide">Experiência Reflexiva</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                O Coração da Ação
              </h2>
              <p className="text-gray-600 mb-4 max-w-xl">
                Uma jornada de autoconhecimento guiada por IA. Explore as necessidades humanas universais
                por trás das suas ações e pensamentos, com reflexões personalizadas.
              </p>
              <Button
                onClick={() => navigate('/heart-of-action')}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                <Heart className="w-4 h-4 mr-2" />
                Iniciar Reflexão
              </Button>
            </div>
          </div>
        </Card>

        {/* Scenarios Grid */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Simuladores de Conversas</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scenarios.map((scenario) => (
            <Card
              key={scenario.id}
              className="overflow-hidden hover:shadow-xl transition-all duration-300 border-2 hover:border-purple-300 cursor-pointer transform hover:scale-105"
              onClick={() => navigate(`/simulator/${scenario.id}`)}
            >
              <div className="p-6">
                {/* Icon */}
                <div className="text-6xl mb-4 text-center">{scenario.character}</div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {scenario.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {scenario.description}
                </p>

                {/* Roles */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-sm">
                    <Users className="w-4 h-4 text-purple-600" />
                    <span className="text-gray-700">
                      <span className="font-semibold">Você:</span> {scenario.userRole}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <MessageCircle className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-700">
                      <span className="font-semibold">IA:</span> {scenario.aiRole}
                    </span>
                  </div>
                </div>

                {/* Situation */}
                <div className="bg-purple-50 p-3 rounded-lg mb-4">
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold">Situação:</span> {scenario.situation}
                  </p>
                </div>

                {/* Button */}
                <Button
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/simulator/${scenario.id}`);
                  }}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar Simulador
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* How it works */}
        <Card className="mt-12 p-8 bg-gradient-to-r from-purple-100 to-purple-50 border-2 border-purple-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Como Funciona?
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-purple-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                1
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Escolha o Cenário</h4>
              <p className="text-sm text-gray-600">
                Selecione uma situação que deseja praticar
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                2
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Converse com a IA</h4>
              <p className="text-sm text-gray-600">
                Responda usando princípios de Inteligência Relacional
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                3
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">5 Mensagens</h4>
              <p className="text-sm text-gray-600">
                Pratique em 5 rodadas de diálogo
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">
                4
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Receba Feedback</h4>
              <p className="text-sm text-gray-600">
                Avaliação detalhada baseada em Inteligência Relacional
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SimulatorsPage;

